import React, { useEffect, useRef, useState } from 'react';
import { SEARCH_BOX_WIDTH, SEARCH_INPUT_FIELD } from '../../constants/inputs';

/**
 * THE free-text search box. One component, one look, one width, every page.
 *
 * Generalised out of `components/gags/GagSearchInput.js` on 2026-08-08 (that
 * file is gone; this replaced it). Nothing about its mechanics was ever
 * gag-specific — only the placeholder and the aria-label were — so the same
 * component now serves the Breakdown, Manage Teams, all three order-list pages,
 * Admin Settings → Users, and all three hires pages — Department Hires,
 * My Hires and Admin Hires (2026-09-13) — and the search box is finally the
 * same size on each of them.
 *
 * Two behaviours it exists to keep intact:
 *
 *  1. **Optional debounce** (`debounceMs`). Defaults to 300 so existing callers
 *     are unchanged.
 *     🔑 **The order pages pass `debounceMs={0}` and that is deliberate.** Order
 *     search is 100% client-side — `utils/orderSearch.js` `orderMatchesSearch`
 *     filtering an already-loaded array (`MyOrders.js`, `DepartmentOrders.js`,
 *     `Approvals.js`) — so a debounce there is pure input lag with nothing to
 *     save. The Breakdown keeps 300ms because typing there refetches
 *     `/api/gags`. At 0 the commit is SYNCHRONOUS, inside the change handler:
 *     no timer at all, not even `setTimeout(…, 0)`, which would still defer the
 *     update by a macrotask and make a keystroke observably async.
 *
 *  2. **The external-resync guard.** If `value` changes from OUTSIDE (a filter
 *     reset, "Clear all"), the local input re-syncs
 *     instead of re-applying stale text `debounceMs` later. `lastEmitted` is the
 *     last value THIS component emitted; when `value` differs from it, the
 *     change came from somewhere else.
 *
 * 🟢 **This is deliberately NOT wired to `hooks/useSearchRequest.js`.** That
 * hook is for a box driving a SERVER request, where a debounce narrows but never
 * closes the stale-response window. This component issues no request — it lifts
 * a string and the parent decides what to do with it — so there is no response
 * to race. Adding `useLatestRequest` here would be cargo-culting. Declared as
 * CLIENT_FILTER in `hooks/useSearchRequest.staleGuardManifest.test.js`.
 *
 * ⚠️ Sizing is `SEARCH_BOX_WIDTH` from `constants/inputs.js` and callers should
 * NOT override it — the whole point of the ruling is that the box is the same on
 * every page. `wrapperClassName` exists for genuine layout needs (e.g. an
 * `ml-auto`), and appends rather than replaces.
 *
 * Props:
 *  - value / onChange(next)  — the committed search term and its setter
 *  - placeholder / ariaLabel — the only things that were ever page-specific
 *  - debounceMs              — 300 default; 0 = commit synchronously
 *  - testId                  — `data-testid` on the <input>
 *  - className               — <input> classes; defaults to SEARCH_INPUT_FIELD
 *  - wrapperClassName        — appended to SEARCH_BOX_WIDTH on the wrapper
 */
export default function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  debounceMs = 300,
  testId,
  className = SEARCH_INPUT_FIELD,
  wrapperClassName = '',
}) {
  const [qLocal, setQLocal] = useState(value);
  // Last value THIS component emitted. If `value` ever differs from it, the
  // change came from outside and the local input must resync.
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setQLocal(value);
    }
  }, [value]);

  useEffect(() => {
    // debounceMs <= 0 commits in handleChange instead; no timer is scheduled,
    // so a keystroke stays synchronous for a client-side filter.
    if (debounceMs <= 0) return undefined;
    const t = setTimeout(() => {
      if (qLocal !== value) {
        lastEmitted.current = qLocal;
        onChange(qLocal);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [qLocal, value, onChange, debounceMs]);

  const handleChange = (e) => {
    const next = e.target.value;
    setQLocal(next);
    if (debounceMs <= 0) {
      lastEmitted.current = next;
      onChange(next);
    }
  };

  return (
    <div className={`relative ${SEARCH_BOX_WIDTH} ${wrapperClassName}`.trim()}>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500"
      >
        🔍
      </span>
      <input
        type="search"
        value={qLocal}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid={testId}
        className={className}
      />
    </div>
  );
}
